import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Zap, Lock, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { isCoordDesktop } from "../lib/desktop";

const REMEMBER_KEY = "thoth_login_remember";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const coordApp = isCoordDesktop();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  // Pre-compila i dati salvati (se l'utente aveva scelto "Ricordami")
  useEffect(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY);
      if (raw) {
        const { e } = JSON.parse(decodeURIComponent(escape(atob(raw))));
        if (e) { setEmail(e); setRemember(true); }
      }
    } catch { /* dati salvati non validi: ignora */ }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      try {
        if (remember) localStorage.setItem(REMEMBER_KEY, btoa(unescape(encodeURIComponent(JSON.stringify({ e: email })))));
        else localStorage.removeItem(REMEMBER_KEY);
      } catch { /* storage non disponibile: ignora */ }
      navigate("/admin", { replace: true });
    } else setError(res.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm fade-up">
        <div className="flex flex-col items-center mb-7">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-700 grid place-items-center shadow-lg shadow-blue-600/40 mb-3">
            <Zap className="w-7 h-7 text-white" fill="currentColor" />
          </div>
          <h1 className="font-head text-2xl font-bold">{coordApp ? "Thoth Coordinamento" : "Thoth"}</h1>
          <p className="text-slate-500 text-sm mt-1" data-testid="login-subtitle">{coordApp ? "Radar Voli · Turni · Pianificazione squadre" : "Facile, veloce e sempre con te!"}</p>
        </div>
        <form onSubmit={submit} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" placeholder="nome@azienda.it"
              className="w-full mt-2 bg-slate-950 border-2 border-slate-600 rounded-xl px-3 py-3 text-sm text-slate-100 focus:outline-none focus:border-blue-500" required />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</label>
            <div className="relative mt-2">
              <input type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" placeholder="••••••••"
                className="w-full bg-slate-950 border-2 border-slate-600 rounded-xl px-3 py-3 pr-11 text-sm text-slate-100 focus:outline-none focus:border-blue-500" required />
              <button type="button" onClick={() => setShowPwd((s) => !s)} data-testid="toggle-password-btn"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-200" aria-label="Mostra password">
                {showPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer select-none" data-testid="login-remember-label">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember-checkbox"
              className="w-4 h-4 rounded border-slate-600 bg-slate-950 text-blue-600 focus:ring-blue-500 focus:ring-offset-0" />
            Ricorda i miei dati di accesso
          </label>
          {error && <p className="text-red-400 text-sm" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="login-submit-btn"
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 py-3 rounded-xl font-semibold flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-4 h-4" />} Accedi
          </button>
          <Link to="/admin/forgot" data-testid="forgot-password-link" className="block text-center text-sm text-blue-400 hover:text-blue-300">
            Hai dimenticato la password?
          </Link>
        </form>
        {!coordApp && <button onClick={() => navigate("/")} data-testid="login-vetrina-link" className="w-full text-center text-sm text-blue-400/80 hover:text-blue-300 mt-4">← Scopri Thoth · Prova gratis 3 giorni</button>}
      </div>
    </div>
  );
}
